<template>
  <vx-card :no-shadow="!cardStyle">
    <form-wizard
      ref="wizard"
      class="wizard-no-title gp-sticky-wizard"
      color="rgba(var(--vs-primary), 1)"
      errorColor="rgba(var(--vs-danger), 1)"
      transition="zoom-fade"
      :title="null"
      :subtitle="null"
      :start-index="0"
      @on-change="onChangeTab">

      <tab-content
        :title="$t('$ReviewsSettingsModule.$BusinessTab.TabTitle')"
        class="mb-5 pt-4"
        icon="fas fa-store"
        :before-change="validateBusinessSelection">
        <div>
          <business-tab
            @place-updated="placeUpdated"/>
        </div>
      </tab-content>

      <tab-content
        :title="$t('$ReviewsSettingsModule.$ConfirmBusinessTab.TabTitle')"
        class="mb-5 pt-4"
        icon="fas fa-check-double"
        :before-change="saveReviewsSettings"
        lazy>
        <div
          v-if="tabIndex === 1"
          class="lg:px-32">
          <confirm-business-tab
            :place-id="placeId"
            :place-name="placeName"
            :place-address="placeAddress"
            @back="$refs.wizard.prevTab()"
            @continue="$refs.wizard.nextTab()"/>
        </div>
      </tab-content>

      <tab-content
        :title="$t('$ReviewsSettingsModule.$SetupCompletedTab.TabTitle')"
        class="mb-5 pt-4"
        icon="fas fa-star-half-alt"
        lazy>
        <div
          v-if="tabIndex === 2"
          class="lg:px-32">
          <setup-completed-tab
            :rating="placeRating"
            :review-link="`https://search.google.com/local/writereview?placeid=${this.placeId}`"
            :hide-request-review-button="hideRequestReviewButton"
            @request-reviews="requestReviews()"/>
        </div>
      </tab-content>

    </form-wizard>

    <!-- Sticky action bar (shared list-page element) -->
    <wizard-buttons
      :wizard-props="stickyWizardProps"
      @cancel="$emit('cancel')"/>
  </vx-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { FormWizard, TabContent } from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';

// Components
import ReviewsSettingsCreateOrEditWizardButtons from '@/views/modules/reviews/ReviewsSettingsCreateOrEditWizardButtons.vue';
import ReviewsSettingsCreateOrEditBusinessTab from '@/views/modules/reviews/ReviewsSettingsCreateOrEditBusinessTab.vue';
import ReviewsSettingsCreateOrEditConfirmBusinessTab from '@/views/modules/reviews/ReviewsSettingsCreateOrEditConfirmBusinessTab.vue';
import ReviewsSettingsCreateOrEditSetupCompletedTab from '@/views/modules/reviews/ReviewsSettingsCreateOrEditSetupCompletedTab.vue';

/**
 * Reviews settings create or edit
 *
 * @module views/modules/reviews/ReviewsSettingsCreateOrEdit
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 * @vue-data {boolean} [cardStyle=true] - indicate if user card style or no
 * @vue-data {boolean} [hideRequestReviewButton=false] - indicate if hide request review button
 * @vue-data {string} [placeId=''] - place id of business
 * @vue-data {string} [placeName=''] - place name of business
 * @vue-data {string} [placeAddress=''] - place address of business
 * @vue-data {number} [placeRating=0] - place rating of business
 * @vue-event {void} onChangeTab - called on change tab
 * @vue-event {void} requestReviews - called on request reviews button
 * @vue-event {void} placeUpdated - called on place updated
 * @vue-event {void} validateBusinessSelection - validate the business selection
 * @vue-event {void} saveReviewsSettings - save the reviews settings
 */
export default {
  name: 'ReviewsSettingsCreateOrEdit',
  i18n: {
    messages: {
      en: {
        RemoveGoogleListingTitle: 'Remove Google listing?',
        RemoveGoogleListingMsg: 'Customers will no longer be sent to Google after 4 or 5 star ratings. You can connect again at any time.',
        RemoveGoogleListingAccept: 'Remove',
        RemoveGoogleListingCancel: 'Keep',
        RemoveGoogleListingSuccessTitle: 'Google listing removed',
        RemoveGoogleListingSuccessMsg: 'Your reviews form will now collect feedback internally only.',
      },
    },
  },
  components: {
    FormWizard,
    TabContent,
    BusinessTab: ReviewsSettingsCreateOrEditBusinessTab,
    ConfirmBusinessTab: ReviewsSettingsCreateOrEditConfirmBusinessTab,
    SetupCompletedTab: ReviewsSettingsCreateOrEditSetupCompletedTab,
    WizardButtons: ReviewsSettingsCreateOrEditWizardButtons,
  },
  props: {
    cardStyle: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideRequestReviewButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      placeId: '',
      placeName: '',
      placeAddress: '',
      placeRating: 0,
      tabIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      reviewsSettings: 'auth/tenantAccountSettingsReviews',
    }),
    stickyWizardProps() {
      const $this = this;
      return {
        activeTabIndex: $this.tabIndex,
        isLastStep: $this.tabIndex === 2,
        showRemove: Boolean($this.reviewsSettings?.placeId || $this.reviewsSettings?.urlReviewGoogle),
        remove() {
          $this.confirmRemoveGoogleListing();
        },
        nextTab() {
          if ($this.$refs.wizard && typeof $this.$refs.wizard.nextTab === 'function') {
            $this.$refs.wizard.nextTab();
          }
        },
        prevTab() {
          if ($this.$refs.wizard && typeof $this.$refs.wizard.prevTab === 'function') {
            $this.$refs.wizard.prevTab();
          }
        },
      };
    },
  },
  methods: {
    ...mapActions({
      updateTenantAccountReviewSettings: 'auth/updateTenantAccountReviewSettings',
      fetchTenantOfUser: 'auth/fetchTenantOfUser',
    }),
    onChangeTab(prevIndex, nextIndex) {
      this.tabIndex = nextIndex;
      this.$refs.wizard.$el.scrollIntoView(true);
    },
    requestReviews() {
      if (this.cardStyle) {
        this.$router.push(`/sms/campaigns/create?messageType=${this.$enums.Campaign.MessageType.TRANSACTIONAL}`);
      } else {
        this.$emit('request-reviews');
      }
    },
    placeUpdated(place) {
      this.placeId = place.id;
      this.placeName = place.name;
      this.placeAddress = place.address;
      this.placeRating = place.rating;
    },
    async validateBusinessSelection() {
      if (this.placeId) {
        return true;
      }

      this.$showGeneralWarningNotification(
        {
          title: this.$t('$ReviewsSettingsModule.$ReviewsSettingsCreateOrEdit.SaveReviewsSettingsNotifyTitle'),
          text: this.$t('$ReviewsSettingsModule.$ReviewsSettingsCreateOrEdit.SaveReviewsSettingsNotifyErrorMsg'),
        },
      );

      return false;
    },
    async saveReviewsSettings() {
      if (!this.placeId) {
        this.$showGeneralWarningNotification(
          {
            title: this.$t('$ReviewsSettingsModule.$ReviewsSettingsCreateOrEdit.SaveReviewsSettingsNotifyTitle'),
            text: this.$t('$ReviewsSettingsModule.$ReviewsSettingsCreateOrEdit.SaveReviewsSettingsNotifyErrorMsg'),
          },
        );

        return false;
      }

      this.$vs.loading({ type: 'radius' });
      await this.updateTenantAccountReviewSettings({
        placeId: this.placeId,
        placeName: this.placeName,
        placeAddress: this.placeAddress,
      });
      this.$vs.loading.close();

      this.$showSuccessActionNotification({
        title: this.$t('$ReviewsSettingsModule.$ReviewsSettingsCreateOrEdit.SaveReviewsSettingsNotifyTitle'),
        text: this.$t('$ReviewsSettingsModule.$ReviewsSettingsCreateOrEdit.SaveReviewsSettingsNotifyOkMsg'),
      });

      await this.fetchTenantOfUser();
      this.$emit('settings-updated');
      return true;
    },
    confirmRemoveGoogleListing() {
      this.$showConfirmWarningDialog({
        title: this.$t('RemoveGoogleListingTitle'),
        text: this.$t('RemoveGoogleListingMsg'),
        acceptText: this.$t('RemoveGoogleListingAccept'),
        cancelText: this.$t('RemoveGoogleListingCancel'),
        accept: () => this.removeGoogleListing(),
      });
    },
    async removeGoogleListing() {
      this.$vs.loading({ type: 'radius' });
      await this.updateTenantAccountReviewSettings({
        placeId: '',
        placeName: '',
        placeAddress: '',
      });
      this.$vs.loading.close();

      this.placeId = '';
      this.placeName = '';
      this.placeAddress = '';
      this.placeRating = 0;
      this.tabIndex = 0;
      if (this.$refs.wizard && typeof this.$refs.wizard.changeTab === 'function') {
        this.$refs.wizard.changeTab(this.tabIndex, 0);
      }

      await this.fetchTenantOfUser();
      this.$showSuccessActionNotification({
        title: this.$t('RemoveGoogleListingSuccessTitle'),
        text: this.$t('RemoveGoogleListingSuccessMsg'),
      });
    },
  },
};
</script>
